import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Cookie, Settings, BarChart3, Shield, Eye, ToggleLeft, Database } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const CookiePolicy = () => {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const cookieTypes = [
    {
      icon: Shield,
      title: "Strictly Necessary Cookies",
      description: "Essential for basic website functionality and security",
      purpose: "These cookies are absolutely necessary for our website to function properly and cannot be disabled. They enable core features like user authentication, security protection, load balancing, and basic navigation. Without these cookies, the website cannot provide the services you have requested.",
      examples: "Session management, CSRF protection, load balancer routing, security tokens, login authentication, form submissions, shopping cart functionality",
      duration: "Session-based (deleted when browser closes) or up to 24 hours for security",
      canDisable: false,
      legalBasis: "Legitimate interest - essential for service delivery",
      dataShared: "Session identifiers (anonymized), security tokens"
    },
    {
      icon: BarChart3,
      title: "Performance & Analytics Cookies",
      description: "Monitor website performance and user behavior patterns",
      purpose: "We use these cookies to collect information about how visitors use our website, which pages are most popular, error patterns, loading times, and user journey analytics. This data helps us identify issues, optimize performance, and improve user experience. All information is collected anonymously.",
      examples: "Google Analytics, page view tracking, bounce rate analysis, conversion funnels, error logging, performance monitoring, A/B testing",
      duration: "Up to 26 months (analytics) / 24 hours (performance)",
      canDisable: true,
      legalBasis: "Legitimate interest with opt-out capability",
      dataShared: "Anonymized usage statistics, aggregated performance metrics"
    },
    {
      icon: Settings,
      title: "Functional & Preference Cookies",
      description: "Remember your settings and enhance user experience",
      purpose: "These cookies remember your choices and preferences to provide a more personalized and convenient experience. They store your language preferences, theme settings, form data, accessibility options, and other customization choices you've made.",
      examples: "Language selection, theme preferences, timezone settings, accessibility options, form auto-fill, recently viewed items, dashboard layouts, notification preferences",
      duration: "Up to 2 years or until manually cleared",
      canDisable: true,
      legalBasis: "Legitimate interest for enhanced user experience",
      dataShared: "User preference settings (non-identifying)"
    },
    {
      icon: Eye,
      title: "Marketing & Advertising Cookies",
      description: "Deliver relevant content and measure campaign effectiveness",
      purpose: "These cookies track your online activity to understand your interests and show you relevant advertisements and content. They help us measure the effectiveness of our marketing campaigns, prevent repeated ads, and provide personalized marketing communications.",
      examples: "Google Ads, Facebook Pixel, conversion tracking, retargeting campaigns, email marketing optimization, social media integration, affiliate tracking",
      duration: "Up to 24 months or as specified by third-party providers",
      canDisable: true,
      legalBasis: "Consent required - opt-in basis",
      dataShared: "Interest categories, marketing interaction data (pseudonymized)"
    },
    {
      icon: ToggleLeft,
      title: "Social Media & Embedding Cookies",
      description: "Enable social sharing and embedded content functionality",
      purpose: "These cookies are set by social media platforms and embedded content providers when you interact with social sharing buttons, embedded videos, chatbots, or other third-party content on our site. They may track your activity across different websites.",
      examples: "Facebook Like buttons, Twitter sharing, LinkedIn integration, YouTube videos, embedded chatbots, social login, content sharing widgets",
      duration: "Varies by provider (typically 1-24 months)",
      canDisable: true,
      legalBasis: "Consent required for non-essential social features",
      dataShared: "Social interaction data, cross-site tracking (per provider policies)"
    },
    {
      icon: Database,
      title: "AI & Machine Learning Cookies",
      description: "Optimize AI responses and improve chatbot performance",
      purpose: "These specialized cookies help our AI systems learn from user interactions to improve chatbot responses, detect usage patterns, optimize AI model performance, and provide better AI-driven features. Data is anonymized and aggregated for AI training purposes.",
      examples: "Chatbot interaction patterns, AI response optimization, machine learning model improvement, conversation flow analysis, user intent detection",
      duration: "Up to 12 months for AI training data",
      canDisable: true,
      legalBasis: "Legitimate interest with opt-out for AI improvement",
      dataShared: "Anonymized interaction patterns, aggregated usage data"
    }
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-950 relative overflow-hidden">
      <SEOHead 
        title="Cookie Policy - BrixBot Website Cookies & Tracking" 
        description="Learn about BrixBot's cookie usage, tracking technologies, and privacy controls. Detailed information about cookie types, third-party services, and your choices."
        isIndexable={true}
      />
      {/* Background Elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-violet-950/30"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl mb-8">
              <Cookie className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Cookie Policy
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Detailed information about cookies, tracking technologies, and your choices. Learn how we use cookies to enhance your experience while respecting your privacy preferences.
            </p>
            <div className="mt-6 text-sm text-gray-500">
              Last updated: August 12, 2025 | Effective: August 12, 2025 | Version 2.0
            </div>
          </div>

          {/* What are cookies */}
          <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm mb-8">
            <CardHeader>
              <CardTitle className="text-2xl text-white">What are Cookies?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-gray-300 space-y-4">
                <p>
                  Cookies are small text files that are stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and analyzing how you use our site.
                </p>
                <p>
                  We use both first-party cookies (set by BrixBot) and third-party cookies (set by our service providers) to enhance functionality, security, and analytics.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Types of Cookies */}
          <div className="space-y-8 mb-8">
            <h2 className="text-3xl font-bold text-white text-center mb-8">Types of Cookies We Use</h2>
            
            {cookieTypes.map((type, index) => (
              <Card key={index} className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-white flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                        <type.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div>{type.title}</div>
                        <div className="text-sm text-gray-400 font-normal mt-1">{type.description}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {type.canDisable ? (
                        <span className="text-sm text-green-400 flex items-center">
                          <ToggleLeft className="w-4 h-4 mr-1" />
                          Optional
                        </span>
                      ) : (
                        <span className="text-sm text-red-400 flex items-center">
                          <Shield className="w-4 h-4 mr-1" />
                          Required
                        </span>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6 text-gray-300">
                    <div>
                      <h4 className="font-semibold text-white mb-2">Purpose:</h4>
                      <p>{type.purpose}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Examples:</h4>
                      <p className="text-sm">{type.examples}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-white mb-2">Duration:</h4>
                        <p className="text-sm">{type.duration}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-2">Legal Basis:</h4>
                        <p className="text-sm">{type.legalBasis}</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Data Shared:</h4>
                      <p className="text-sm text-blue-300">{type.dataShared}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Comprehensive Third-party Services */}
          <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm mb-8">
            <CardHeader>
              <CardTitle className="text-2xl text-white">Third-Party Services & Data Sharing</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-gray-300 space-y-6">
                <div>
                  <h4 className="font-semibold text-white mb-3">Analytics & Performance Monitoring</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Google Analytics 4:</strong> Website traffic analysis, user behavior tracking, conversion measurement</li>
                    <li><strong>Google Tag Manager:</strong> Tag and pixel management, marketing automation</li>
                    <li><strong>Hotjar:</strong> User experience analysis, heatmaps, session recordings (anonymized)</li>
                    <li><strong>New Relic:</strong> Application performance monitoring, error tracking</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Marketing & Advertising Platforms</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Google Ads:</strong> Advertising campaigns, conversion tracking, retargeting campaigns</li>
                    <li><strong>Facebook/Meta Pixel:</strong> Social media advertising, audience building, conversion optimization</li>
                    <li><strong>LinkedIn Insight Tag:</strong> Professional network advertising, B2B targeting</li>
                    <li><strong>Microsoft Advertising:</strong> Search engine marketing, Bing Ads campaigns</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Business & Communication Tools</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Stripe:</strong> Secure payment processing, fraud prevention, transaction management</li>
                    <li><strong>PayPal:</strong> Alternative payment processing, subscription management</li>
                    <li><strong>Intercom/Zendesk:</strong> Customer support chat, helpdesk functionality</li>
                    <li><strong>Mailchimp/SendGrid:</strong> Email marketing, transactional emails, newsletter delivery</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Content & Media Services</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>YouTube/Vimeo:</strong> Embedded videos, media content delivery</li>
                    <li><strong>Cloudflare:</strong> Content delivery network, security, performance optimization</li>
                    <li><strong>AWS CloudFront:</strong> Content delivery, global distribution</li>
                    <li><strong>Font Services:</strong> Google Fonts, Adobe Fonts for typography</li>
                  </ul>
                </div>

                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-400 mb-2">Third-Party Privacy Policies</h4>
                  <p className="text-sm">
                    Each third-party service has its own privacy policy and cookie practices. We recommend reviewing their policies:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-sm">
                    <li>Google: policies.google.com</li>
                    <li>Facebook/Meta: facebook.com/privacy</li>
                    <li>LinkedIn: linkedin.com/legal/privacy-policy</li>
                    <li>Stripe: stripe.com/privacy</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Advanced Cookie Management */}
          <Card className="bg-gradient-to-r from-violet-900/30 to-purple-900/30 border-violet-500/20 backdrop-blur-sm mb-8">
            <CardHeader>
              <CardTitle className="text-2xl text-white flex items-center">
                <Settings className="w-6 h-6 mr-3 text-violet-400" />
                Advanced Cookie Management & Your Choices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-gray-300 space-y-6">
                <div>
                  <h4 className="font-semibold text-white mb-3">Browser-Level Cookie Controls</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-violet-300 mb-2">Chrome:</h5>
                      <p className="text-sm">Settings → Privacy and Security → Cookies and other site data</p>
                    </div>
                    <div>
                      <h5 className="font-medium text-violet-300 mb-2">Firefox:</h5>
                      <p className="text-sm">Settings → Privacy & Security → Cookies and Site Data</p>
                    </div>
                    <div>
                      <h5 className="font-medium text-violet-300 mb-2">Safari:</h5>
                      <p className="text-sm">Preferences → Privacy → Manage Website Data</p>
                    </div>
                    <div>
                      <h5 className="font-medium text-violet-300 mb-2">Edge:</h5>
                      <p className="text-sm">Settings → Cookies and site permissions → Cookies and site data</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Granular Cookie Controls</h4>
                  <p className="mb-3">You can manage cookies with different levels of control:</p>
                  <ul className="list-disc list-inside ml-4 space-y-2">
                    <li><strong>Block All Cookies:</strong> Prevents all cookies (may break website functionality)</li>
                    <li><strong>Block Third-Party Cookies:</strong> Allows first-party, blocks third-party tracking</li>
                    <li><strong>Block Specific Categories:</strong> Selective blocking of marketing, analytics, etc.</li>
                    <li><strong>Site-Specific Settings:</strong> Custom rules for individual websites</li>
                    <li><strong>Automatic Deletion:</strong> Delete cookies when browser closes or after time period</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Privacy-Enhanced Browsing</h4>
                  <p className="mb-3">Enhanced privacy options available:</p>
                  <ul className="list-disc list-inside ml-4 space-y-2">
                    <li><strong>Private/Incognito Mode:</strong> Cookies deleted when session ends</li>
                    <li><strong>Enhanced Tracking Protection:</strong> Built-in browser protection (Firefox, Safari)</li>
                    <li><strong>Cookie Auto-Delete Extensions:</strong> Automatically remove cookies when tabs close</li>
                    <li><strong>Privacy-Focused Browsers:</strong> Brave, DuckDuckGo, Tor Browser</li>
                    <li><strong>VPN Services:</strong> Additional privacy layer for browsing</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Opt-Out Tools & Services</h4>
                  <p className="mb-3">Industry-standard opt-out mechanisms:</p>
                  <ul className="list-disc list-inside ml-4 space-y-2">
                    <li><strong>Google Analytics Opt-out:</strong> tools.google.com/dlpage/gaoptout</li>
                    <li><strong>Facebook Off-Facebook Activity:</strong> facebook.com/off_facebook_activity</li>
                    <li><strong>Digital Advertising Alliance:</strong> optout.aboutads.info</li>
                    <li><strong>Network Advertising Initiative:</strong> optout.networkadvertising.org</li>
                    <li><strong>Your Online Choices (EU):</strong> youronlinechoices.eu</li>
                  </ul>
                </div>

                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
                  <h4 className="font-semibold text-red-400 mb-2">Impact of Disabling Cookies</h4>
                  <p className="text-sm mb-2">Disabling cookies may affect your experience:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1 text-sm">
                    <li>Inability to login or maintain logged-in sessions</li>
                    <li>Loss of personalized settings and preferences</li>
                    <li>Reduced functionality of interactive features</li>
                    <li>Repetitive privacy notices and consent requests</li>
                    <li>Inability to complete purchases or form submissions</li>
                    <li>Degraded chatbot performance and AI responses</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Legal & Compliance Information */}
          <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-white">Legal Compliance & Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-gray-300 space-y-6">
                <div>
                  <h4 className="font-semibold text-white mb-3">Regulatory Compliance</h4>
                  <p className="mb-2">Our cookie practices comply with:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong>EU ePrivacy Directive</strong> (Cookie Law)</li>
                    <li><strong>GDPR Article 7</strong> (Consent requirements)</li>
                    <li><strong>CCPA</strong> (California Consumer Privacy Act)</li>
                    <li><strong>COPPA</strong> (Children's Online Privacy Protection)</li>
                    <li><strong>PIPEDA</strong> (Canada's privacy legislation)</li>
                    <li><strong>Slovak Data Protection Laws</strong></li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Cookie Consent Management</h4>
                  <p className="mb-2">We implement consent management through:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Clear, prominent consent banners</li>
                    <li>Granular consent options for different cookie categories</li>
                    <li>Easy withdrawal of consent mechanisms</li>
                    <li>Consent logging and documentation</li>
                    <li>Regular consent renewal requests</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Questions About Cookies & Privacy</h4>
                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <p className="mb-3">For cookie-related questions, privacy concerns, or to exercise your rights:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p><strong>Privacy Team:</strong> <span className="text-violet-400">support@brixbot.ai</span></p>
                        <p><strong>Cookie Support:</strong> <span className="text-violet-400">support@brixbot.ai</span></p>
                        <p><strong>General Support:</strong> <span className="text-violet-400">support@brixbot.ai</span></p>
                      </div>
                      <div>
                        <p><strong>Phone:</strong> <span className="text-violet-400">+421915721942</span></p>
                        <p><strong>Response Time:</strong> 2-3 business days</p>
                        <p><strong>Business Hours:</strong> Mon-Fri, 9:00-17:00 CET</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Data Protection Authorities</h4>
                  <p className="mb-2">You can file complaints with relevant authorities:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong>Slovakia:</strong> Úrad na ochranu osobných údajov (dataprotection.gov.sk)</li>
                    <li><strong>EU/EEA:</strong> Your local data protection authority</li>
                    <li><strong>UK:</strong> Information Commissioner's Office (ico.org.uk)</li>
                    <li><strong>USA:</strong> Federal Trade Commission (ftc.gov)</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Cookie Policy Updates</h4>
                  <p className="mb-2">We may update this Cookie Policy to reflect:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Changes in cookie technologies or practices</li>
                    <li>New third-party services or integrations</li>
                    <li>Updates to privacy laws and regulations</li>
                    <li>Improvements to our cookie management systems</li>
                  </ul>
                  <p className="mt-2 text-sm text-yellow-300">
                    Significant changes will be communicated via email and prominent website notices.
                  </p>
                </div>

                <div className="border-t border-gray-600 pt-4">
                  <p className="text-sm text-gray-400">
                    <strong>Policy Version:</strong> 2.0<br/>
                    <strong>Last Updated:</strong> August 12, 2025<br/>
                    <strong>Effective Date:</strong> August 12, 2025<br/>
                    <strong>Next Review:</strong> December 2025<br/>
                    <strong>Total Cookie Types:</strong> 6 categories | <strong>Compliance Framework:</strong> GDPR, CCPA, ePrivacy
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;